import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { tickerDetailService } from '../../services/tickerDetailService';
import { SectionSkeleton, SectionError } from './CompanyProfile';
import { EarningsChart } from './earnings/EarningsChart';
import { EarningsStats } from './earnings/EarningsStats';
import { prepareEarnings, summarise, windowEarnings } from './earnings/prepareEarnings';
import { RANGES, DEFAULT_RANGE } from './earnings/constants';

interface Props { symbol: string; }

/** Which side of the session a company reports on, or null if unstated. */
function reportWindow(time: string | null | undefined): 'bmo' | 'amc' | null {
  const t = (time ?? '').toLowerCase();
  if (t.includes('bmo') || t.includes('before')) return 'bmo';
  if (t.includes('amc') || t.includes('after')) return 'amc';
  return null;
}

/**
 * Sun or moon on its own, sized and coloured by the caller.
 *
 * Lives here rather than on the Calendar page so both mark a reporting window
 * with the same glyph — two definitions would drift.
 */
export function ReportIcon({ time, className = 'w-4 h-4' }: {
  time: string | null | undefined;
  className?: string;
}): React.ReactElement | null {
  const w = reportWindow(time);
  if (!w) return null;

  return w === 'bmo' ? (
    // Full sun — no horizon line, so it cannot be misread as a sunset.
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="12" r="4" />
      <path
        d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"
        strokeLinecap="round"
      />
    </svg>
  ) : (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path d="M21 12.8A9 9 0 1111.2 3a7 7 0 009.8 9.8z" strokeLinejoin="round" />
    </svg>
  );
}

const DASH = '—';

function fmtEps(n: number): string {
  return `$${n.toFixed(2)}`;
}

/** Revenue in the largest unit that keeps the number short. */
function fmtRevenue(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  return `$${n.toFixed(0)}`;
}

function fmtPct(n: number | null): string {
  if (n == null) return DASH;
  return `${n >= 0 ? '+' : ''}${(n * 100).toFixed(1)}%`;
}

export function Earnings({ symbol }: Props): React.ReactElement {
  const [range, setRange] = useState(DEFAULT_RANGE);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['tickerEarnings', symbol],
    queryFn: () => tickerDetailService.getEarnings(symbol),
    staleTime: 4 * 60 * 60 * 1000,
    enabled: !!symbol,
  });

  const rows = useMemo(() => data?.data ?? [], [data]);
  const quarters = RANGES.find((r) => r.key === range)?.quarters ?? 12;
  const rangeLabel = RANGES.find((r) => r.key === range)?.label ?? '3Y';

  const { history, next, summary, charted } = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const points = prepareEarnings(rows, today);
    const win = windowEarnings(points, quarters);
    return {
      history: win.history,
      next: win.next,
      summary: summarise(win.history, win.next),
      // The scheduled quarter rides on the end of the charts as a ghost bar,
      // so the panels carry one more column than the beat-rate window.
      charted: win.next ? [...win.history, win.next] : win.history,
    };
  }, [rows, quarters]);

  if (isLoading) return <SectionSkeleton />;
  if (isError) return <SectionError message="Failed to load earnings" />;

  return (
    <section className="p-4 border-b border-dark-border">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-300">Earnings</h2>
        <div className="flex overflow-hidden rounded border border-dark-border text-xs">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`px-3 py-1 transition-colors ${
                range === r.key
                  ? 'bg-accent-yellow font-semibold text-hot-tag-text'
                  : 'bg-dark-bg text-gray-400 hover:text-gray-200'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {!charted.length ? (
        <p className="text-sm text-gray-400">No earnings data</p>
      ) : (
        <>
          <EarningsStats summary={summary} windowLabel={rangeLabel} />

          <div className="mb-5 space-y-5">
            <EarningsChart title="EPS" measure="eps" points={charted} format={fmtEps} />
            <EarningsChart title="Revenue" measure="revenue" points={charted} format={fmtRevenue} />
          </div>

          <details className="group" open>
            <summary className="mb-2 cursor-pointer list-none text-xs font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-200">
              <span className="inline-block transition-transform group-open:rotate-90">&rsaquo;</span>{' '}
              Reported Quarters
            </summary>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left text-xs">
                <thead>
                  <tr className="border-b border-dark-border">
                    {['Date', 'EPS Est.', 'EPS Actual', 'Beat/Miss', 'EPS Surp.', 'Rev. Est.', 'Rev. Actual', 'Rev. Surp.'].map((h) => (
                      <th key={h} className="py-1.5 pr-4 font-medium text-gray-400">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Newest first: the table is a lookup, where the chart is a trend. */}
                  {[...history].reverse().map((p) => {
                    const beat = p.epsSurprise == null ? null : p.epsSurprise >= 0;
                    return (
                      <tr key={p.date} className="border-b border-dark-border/50 hover:bg-dark-surface/50">
                        <td className="py-1.5 pr-4 font-numeric text-white">{p.date}</td>
                        <td className="py-1.5 pr-4 font-numeric text-gray-400">
                          {p.epsEstimate ?? DASH}
                        </td>
                        <td className="py-1.5 pr-4 font-numeric text-white">{p.epsActual ?? DASH}</td>
                        <td className="py-1.5 pr-4 font-medium">
                          {beat === null ? DASH : (
                            <span className={beat ? 'text-bull-blue' : 'text-bear-red'}>
                              {beat ? '▲ Beat' : '▼ Miss'}
                            </span>
                          )}
                        </td>
                        <td className={`py-1.5 pr-4 font-numeric ${
                          p.epsSurprise == null ? 'text-gray-400' : p.epsSurprise >= 0 ? 'text-bull-blue' : 'text-bear-red'
                        }`}>
                          {fmtPct(p.epsSurprise)}
                        </td>
                        <td className="py-1.5 pr-4 font-numeric text-gray-400">
                          {p.revenueEstimate != null ? fmtRevenue(p.revenueEstimate) : DASH}
                        </td>
                        <td className="py-1.5 pr-4 font-numeric text-white">
                          {p.revenueActual != null ? fmtRevenue(p.revenueActual) : DASH}
                        </td>
                        <td className={`py-1.5 pr-4 font-numeric ${
                          p.revenueSurprise == null ? 'text-gray-400' : p.revenueSurprise >= 0 ? 'text-bull-blue' : 'text-bear-red'
                        }`}>
                          {fmtPct(p.revenueSurprise)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </details>
        </>
      )}
      {next && !charted.length && (
        <p className="mt-2 text-xs text-gray-400">Next report: {next.date}</p>
      )}
    </section>
  );
}
